import React, { useEffect } from 'react';
import { BrowserRouter as Router, Link, Route, Switch, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ContactForm from './components/ContactForm';
import BlogIndex from './blog/BlogIndex';
import BlogPost from './blog/BlogPost';
import PostTile from './blog/PostTile';
import posts from './blog/posts';
import usePageMeta from './blog/usePageMeta';

const projects = [
  {
    title: 'Farshore',
    kind: 'Browser game',
    tagline: 'A first-person exploration game',
    text: 'Eight chapters that begin inside a sealed, overgrown Conservatory, about an hour end to end. Built with Three.js and TypeScript, no game engine, no asset packs, every system hand-rolled.',
    image: '/blog/farshore/root-court.jpg',
    imageAlt: 'A great tree in a circular stone court, light rising from its canopy',
    badge: 'Open playtest through October',
    links: [
      { label: 'Play it free', href: 'https://farshore.netlify.app/' },
      { label: 'Join the playtest', href: '/blog/farshore-playtest' },
    ],
  },
  {
    title: 'Vertigo',
    kind: 'Browser game',
    tagline: 'Six climbs, from sunset to morning',
    text: "Six first-person climbs: sandstone pillars above the clouds, a drowned gothic city in a storm, a monks' crag climbed by night into dawn, a copper pit worked down in rings, a crossing of the range by the old tin road, and a waterfall with no water. Find what's hidden off the route, then race your own ghost to the top.",
    image: '/blog/vertigo/summit.jpg',
    imageAlt: 'The summit bell under a red frame at sunset, the pillar forest glowing in the haze below',
    badge: 'Open playtest through November',
    links: [
      { label: 'Play it free', href: 'https://vertigo-climb.netlify.app/' },
      { label: 'Join the playtest', href: '/blog/vertigo-playtest' },
    ],
  },
  {
    title: 'PodBrowser',
    kind: 'Web app',
    tagline: 'A morning paper for podcasts',
    text: 'Every episode of the crypto, markets and tech podcasts we follow, transcribed and summarised. A daily briefing of what they all said, and an archive you can ask questions of, with every answer citing its source.',
    image: '/blog/podbrowser/briefing.jpg',
    imageAlt: 'The PodBrowser morning briefing: themes with headlines and points, each tagged with the show it came from',
    imagePosition: 'left top',
    badge: 'New daily briefing',
    links: [
      { label: 'Read the briefing', href: 'https://podbrowser.ai/' },
      { label: 'Read about it', href: '/blog/podbrowser' },
    ],
  },
  {
    title: 'Quiet Bamboo',
    kind: 'YouTube channel',
    tagline: 'Calm stories for a loud world',
    text: 'Gentle, narrated retellings of Daoist, Buddhist and Confucian stories, with watercolour and ink illustrations. Ten quiet minutes at a time.',
    image: 'https://i.ytimg.com/vi/I94oQh5PO1I/maxresdefault.jpg',
    imageAlt: 'A watercolour of a lone boat on a calm river beneath misty mountains',
    imagePosition: 'left center',
    badge: '2.2K subscribers · 142K views on one story',
    links: [
      { label: 'Watch', href: 'https://www.youtube.com/@quietbambooo' },
      { label: 'Channel update', href: '/blog/quiet-bamboo-update' },
    ],
  },
  {
    title: 'Coach Ginge',
    kind: 'YouTube channel',
    tagline: 'Brain breaks for kids',
    text: 'An AI-animated ginger cat leading free, follow-along brain breaks for children, a few minutes each, standing up. A tribute to Grandad, the cat who supervised all of our workouts.',
    image: '/blog/coach-ginge/20250412-161522.jpg',
    imageAlt: 'Grandad, a ginger-and-white Persian cat, peering over a row of mint plants',
    imagePosition: 'center 55%',
    badge: 'Five Halloween brain breaks',
    links: [
      { label: 'Visit coachginge.com', href: 'https://coachginge.com' },
      { label: 'Read the story', href: '/blog/coach-ginge' },
    ],
  },
  {
    title: "The China You Haven't Seen",
    kind: 'YouTube channel',
    tagline: 'Chinese tech, from the inside',
    text: 'Hands-on reporting on the phones, drones, EVs and apps that everyday life in China runs on. We move there next year; the channel is live now.',
    image: 'https://i.ytimg.com/vi/5eKPD3lkjuw/maxresdefault.jpg',
    imageAlt: "Thumbnail for China's Delivery Drones Are Real, But Not How You Think",
    badge: 'New channel',
    links: [
      { label: 'Watch', href: 'https://www.youtube.com/@TheChinaNotSeen' },
      { label: 'Why we started it', href: '/blog/the-china-you-havent-seen' },
    ],
  },
];

const strip = [
  { label: 'Farshore', href: '#work', image: '/blog/farshore/conservatory.jpg', position: 'center top' },
  { label: 'Coach Ginge', href: '#work', image: '/blog/coach-ginge/20250516-003637.jpg', position: 'center 35%' },
  { label: 'Quiet Bamboo', href: '#work', image: 'https://i.ytimg.com/vi/llTJIL13Cws/maxresdefault.jpg' },
];

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function ProjectLink({ link, primary }) {
  const className = `btn ${primary ? 'btn-primary' : 'btn-ghost'}`;
  return link.href.startsWith('/') ? (
    <Link className={className} to={link.href}>{link.label}</Link>
  ) : (
    <a className={className} href={link.href}>{link.label}</a>
  );
}

function Home() {
  usePageMeta();
  const latest = posts.slice(0, 3);

  return (
    <>
      <section className="hero">
        <div className="container">
          <span className="eyebrow">Independent studio · Hong Kong</span>
          <h1>
            We make <em>games</em>, <em>stories</em> and{' '}
            <span className="gradient-text">things for the internet.</span>
          </h1>
          <p className="hero-sub">
            Oneshoo is a small studio that builds its own projects:
            first-person browser games, a handful of YouTube channels, and
            whatever interests us next. We occasionally take on client work too.
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary" href="#work">See what we&apos;ve made</a>
            <a className="btn btn-ghost" href="#contact">Get in touch</a>
          </div>
          <div className="hero-strip" aria-hidden="true">
            {strip.map((s) => (
              <a key={s.label} href={s.href} tabIndex={-1}>
                <img
                  src={s.image}
                  alt=""
                  style={s.position ? { objectPosition: s.position } : undefined}
                />
                <span>{s.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="work" className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Projects</span>
              <h2>What we make</h2>
              <p className="section-sub">
                Our own projects, built and shipped from this studio.
              </p>
            </div>
          </div>
          <div className="project-list">
            {projects.map((p) => (
              <article className="project" key={p.title}>
                <div className="project-media">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    loading="lazy"
                    style={p.imagePosition ? { objectPosition: p.imagePosition } : undefined}
                  />
                </div>
                <div className="project-body">
                  <span className="project-kind">{p.kind}</span>
                  <h3>{p.title}</h3>
                  <p className="project-tagline">{p.tagline}</p>
                  <p className="project-text">{p.text}</p>
                  {p.badge && <span className="project-badge">{p.badge}</span>}
                  <div className="project-actions">
                    {p.links.map((l, i) => (
                      <ProjectLink key={l.label} link={l} primary={i === 0} />
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Blog</span>
              <h2>Latest from the studio</h2>
            </div>
            <Link className="section-link" to="/blog">All posts →</Link>
          </div>
          <div className="post-grid">
            {latest.map((post) => (
              <PostTile key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="section">
        <div className="container about-grid">
          <div>
            <span className="eyebrow">About</span>
            <h2>A small studio that ships its own work</h2>
          </div>
          <div className="about-copy">
            <p>
              Oneshoo Ltd is an independent studio registered in Hong Kong. We
              build our own products and projects first: games, AI-driven
              content, small tools and experiments.
            </p>
            <p>
              Underneath it all is hands-on engineering across AI, software and
              graphics. If you have a problem that needs building rather than
              advising, we occasionally take on client work.
            </p>
            <div className="about-facts">
              <div className="about-fact">
                <strong>6</strong>
                <span>projects live</span>
              </div>
              <div className="about-fact">
                <strong>142K</strong>
                <span>views on our most-watched story</span>
              </div>
              <div className="about-fact">
                <strong>0</strong>
                <span>game engines harmed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section section-contact">
        <div className="container">
          <div className="contact-card">
            <span className="eyebrow">Contact</span>
            <h2>Let&apos;s talk</h2>
            <p className="section-sub">
              Working with us, a project idea, or just curious about something
              we&apos;ve made? Say hello.
            </p>
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="site">
        <Header />
        <Switch>
          <Route exact path="/" component={Home} />
          <Route exact path="/blog" component={BlogIndex} />
          <Route path="/blog/:slug" component={BlogPost} />
          <Route component={BlogPost} />
        </Switch>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
